/**
 * @fileoverview Application Routes Index
 *
 * Central routing configuration. Lazy-loaded components live in `./lazyComponents`;
 * area-specific `<Route>` groups live in `./bundles/*`.
 *
 * React Router v6 requires direct children of `<Routes>` to be `<Route>` or
 * `<React.Fragment>` only — not custom wrapper components — so bundle modules
 * export functions that return fragments and we invoke them below: `{Foo()}`.
 */
import React, { Suspense } from 'react';
import { Routes } from 'react-router-dom';
import PrivateRoute from '../components/auth/PrivateRoute';
import RequireCapability from '../components/auth/RequireCapability';
import { LoadingFallback } from './lazyComponents';
import { CorePublicAppRoutes } from './bundles/CorePublicAppRoutes';
import { ProjectRoutes } from './bundles/ProjectRoutes';
import { TenderRoutes } from './bundles/TenderRoutes';
import { FinanceBudgetCampDailiesRoutes } from './bundles/FinanceBudgetCampDailiesRoutes';
import { ExecutivePlatformAdminRoutes } from './bundles/ExecutivePlatformAdminRoutes';
import { HrRoutes } from './bundles/HrRoutes';
import { InventoryProcurementRoutes } from './bundles/InventoryProcurementRoutes';
import { CateringRoutes } from './bundles/CateringRoutes';
import { AdministrationRoutes } from './bundles/AdministrationRoutes';
import { QhseOperationsRoutes } from './bundles/QhseOperationsRoutes';
import { SettingsHelpCatchAllRoutes } from './bundles/SettingsHelpCatchAllRoutes';

const AppRoutes = () => {
  const withCapability = (capability, element) => (
    <PrivateRoute>
      <RequireCapability capability={capability}>{element}</RequireCapability>
    </PrivateRoute>
  );
  const withAnyCapability = (capabilities, element) => (
    <PrivateRoute>
      <RequireCapability anyOf={capabilities}>{element}</RequireCapability>
    </PrivateRoute>
  );

  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        {CorePublicAppRoutes()}
        {ProjectRoutes({ withCapability })}
        {TenderRoutes({ withCapability })}
        {FinanceBudgetCampDailiesRoutes({ withCapability, withAnyCapability })}
        {ExecutivePlatformAdminRoutes({ withCapability, withAnyCapability })}
        {HrRoutes({ withCapability })}
        {InventoryProcurementRoutes({ withCapability })}
        {CateringRoutes({ withCapability })}
        {AdministrationRoutes()}
        {QhseOperationsRoutes({ withCapability, withAnyCapability })}
        {SettingsHelpCatchAllRoutes()}
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
